import { useEffect, useReducer } from "react";
import "../../../app/App.scss";
import { useIntl } from "react-intl";
import { sendRequest } from "../../../common/http";
import { IntlProvider } from "react-intl";
import ENGLISH from "../../../i18n/languages/en-US.json";
import { useParams } from "react-router-dom";
import { pig } from "../../../static/js/pig.js";
import Header from "./Header";
import Loader from "../../common/elements/Loader";
import translate from "@/i18n/translate";
import { dateWithoutTimezone } from "../../common/elements/ReportingYears";
import { formatDate } from "../../common/helper";
import ApiError from "../../common/elements/ApiError";
import { AccomplishmentsChart } from "../AccomplishmentsChart";
import FacultySuccessCollegesTable from "./FacultySuccessCollegesTable";
import UnauthorizedComponent from "@/features/insights/UnauthorizedComponent";
import { defaultReportingYear } from "../../common/interfaces/Interface";

interface ReportingYear {
  endDate: string;
  name: string;
  startDate: string;
  uuid: string;
}

interface Accomplishment {
  category: string;
  count: number;
  order: number;
}

export interface AccomplishmentData {
  CONGRANT: number;
  INTELLCONT: number;
  INTELLPROP: number;
  PERFORM_EXHIBIT: number;
  PRESENT: number;
  Total: number;
}
interface AccomplishmentsByCategoryData {
  accomplishments: {
    [collegeName: string]: AccomplishmentData;
  };
}
interface FacultySuccessHtmlState {
  locale: string;
  messages: object;
  reportingYear: ReportingYear | null;
  reportingYearAuth: Boolean;
  accomplishments: Accomplishment[];
  totalAccomplishments: number | null | undefined;
  accomplishmentsByCategory: AccomplishmentsByCategoryData | null;
  summaryError: boolean;
  accomplishmentsError: boolean;
  reportingYearError: boolean;
}
interface FacultySuccessAction {
  type: string;
  reportingYear: ReportingYear | null;
  locale: string | null;
  scholarshipSummary: Accomplishment[];
  accomplishmentsByCategory: AccomplishmentsByCategoryData | null;
  summaryError: boolean;
  accomplishmentsError: boolean;
  reportingYearError: boolean;
}

interface UrlParams {
  reporting_year_uuid: string;
  mapping_id: string;
}

const facultySuccessAction = {
  type: "",
  reportingYear: defaultReportingYear,
  scholarshipSummary: [],
  locale: "en-US",
  accomplishmentsByCategory: null,
  summaryError: false,
  accomplishmentsError: false,
  reportingYearError: false
};

const setScholarshipSummary = (
  state: FacultySuccessHtmlState,
  scholarshipSummary: Accomplishment[]
) => {
  const total = scholarshipSummary.find(
    (acc: Accomplishment) => acc.category === "Total"
  );
  const accomplishments: Array<Accomplishment> = scholarshipSummary
    .filter((acc: Accomplishment) => acc.category !== "Total")
    .sort((acc1: Accomplishment, acc2: Accomplishment) => {
      return acc1.order > acc2.order ? 1 : -1;
    });
  return {
    ...state,
    accomplishments: accomplishments,
    totalAccomplishments: total && total.count
  };
};
const reducer = (
  state: FacultySuccessHtmlState,
  action: FacultySuccessAction
) => {
  switch (action.type) {
    case "setReportingYear":
      if (action.reportingYear) {
        return { ...state, reportingYear: action.reportingYear };
      } else {
        return { ...state, reportingYear: null, reportingYearAuth: false };
      }

    case "setLocale":
      return setLocale(state, action.locale);
    case "setScholarshipSummary":
      return setScholarshipSummary(state, action.scholarshipSummary);
    case "setAccomplishmentsByCategory":
      return {
        ...state,
        accomplishmentsByCategory: action.accomplishmentsByCategory
      };
    case "setSummaryError":
      return { ...state, summaryError: true };
    case "setAccomplishmentsError":
      return { ...state, accomplishmentsError: true };
    case "setReportingYearError":
      return { ...state, reportingYearError: true };

    default:
      return state;
  }
};

const setLocale = (state: FacultySuccessHtmlState, locale: string | null) => {
  switch (locale) {
    case "pig": {
      return { ...state, locale: "en-US", messages: pig(ENGLISH) };
    }
    default: {
      return { ...state, locale: "en-US", messages: ENGLISH };
    }
  }
};

export interface FacultySuccessExportViewProps {
  auth: boolean | undefined;
}

export default function FacultySuccessExportView({
  auth
}: FacultySuccessExportViewProps): JSX.Element {
  const initialState = {
    locale: "en-US",
    messages: ENGLISH,
    reportingYear: null,
    reportingYearAuth: true,
    accomplishments: [],
    totalAccomplishments: null,
    accomplishmentsByCategory: null,
    summaryError: false,
    accomplishmentsError: false,
    reportingYearError: false
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const { reporting_year_uuid: reportingYearUuid, mapping_id: mappingId } =
    useParams<UrlParams>();

  useEffect(() => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale");
    dispatch({
      ...facultySuccessAction,
      type: "setLocale",
      locale: locale
    });
  }, []);

  useEffect(() => {
    const applyResult = (result: ReportingYear | null) => {
      dispatch({
        ...facultySuccessAction,
        type: "setReportingYear",
        reportingYear: result
      });
    };
    const onError = () => {
      dispatch({
        ...facultySuccessAction,
        type: "setReportingYearError",
        reportingYearError: true
      });
    };

    auth &&
      sendRequest(
        {
          uri: `/sa/reporting_years/${reportingYearUuid}`,
          camelize: true
        },
        applyResult,
        onError
      );
  }, [auth]);

  useEffect(() => {
    if (mappingId && state.reportingYear) {
      const applyResult = ({
        scholarshipSummary
      }: {
        scholarshipSummary: Accomplishment[];
      }) => {
        dispatch({
          ...facultySuccessAction,
          type: "setScholarshipSummary",
          scholarshipSummary: scholarshipSummary
        });
      };
      const onError = () => {
        dispatch({
          ...facultySuccessAction,
          type: "setSummaryError",
          summaryError: true
        });
      };
      auth &&
        sendRequest(
          {
            uri:
              "/faculty_success/scholarship_summary" +
              "?mapping_id=" +
              mappingId +
              "&start_date=" +
              dateWithoutTimezone(state.reportingYear?.startDate) +
              "&end_date=" +
              dateWithoutTimezone(state.reportingYear?.endDate),
            camelize: true
          },
          applyResult,
          onError
        );
    }
  }, [auth, mappingId, state.reportingYear]);

  useEffect(() => {
    if (mappingId && state.reportingYear) {
      const applyResult = (result: AccomplishmentsByCategoryData) => {
        dispatch({
          ...facultySuccessAction,
          type: "setAccomplishmentsByCategory",
          accomplishmentsByCategory: result
        });
      };
      const onError = () => {
        dispatch({
          ...facultySuccessAction,
          type: "setAccomplishmentsError",
          accomplishmentsError: true
        });
      };

      auth &&
        sendRequest(
          {
            uri:
              "/faculty_success/accomplishments/" +
              "?mapping_id=" +
              mappingId +
              "&start_date=" +
              dateWithoutTimezone(state.reportingYear?.startDate) +
              "&end_date=" +
              dateWithoutTimezone(state.reportingYear?.endDate)
          },
          applyResult,
          onError
        );
    }
  }, [auth, mappingId, state.reportingYear]);

  const Accomplishments = () => {
    return (
      <div className="">
        <div className="heading-label">
          {translate("app.faculty.htmlChartLabel", {
            category: "Institution"
          })}
        </div>
        {!state.summaryError && (
          <div className="flex-row accomplishments-container">
            <div className="">
              <div className="acomplishments-heading-html">
                {translate("app.faculty.totalAccomplishments")}
              </div>

              <div className="accomplishments-count-html">
                {state.totalAccomplishments}
              </div>
              {state.reportingYear && state.reportingYear.startDate && (
                <div className="accomplishments-years">
                  {translate("app.faculty.accomplishmentsYear", {
                    startDate: `${formatDate(state.reportingYear?.startDate)}`,
                    endDate: `${formatDate(state.reportingYear?.endDate)}`
                  })}
                </div>
              )}
            </div>
            <div className="chart-fs-html" id="scholarship-summary-export">
              <AccomplishmentsChart
                accomplishments={state.accomplishments}
                forHtml={true}
              />
            </div>
          </div>
        )}
        {state.summaryError && <ApiError />}
      </div>
    );
  };
  return (
    <div className="pagewrapper faculty-html-container">
      <div className="insights-hub-body flex-row">
        <div className="nav-main">
          <div className="contentwrapper">
            {!state.reportingYearError && (
              <main id="maincontent" className="main-container">
                {state.messages && (
                  <IntlProvider locale={state.locale} messages={state.messages}>
                    {auth &&
                    state.reportingYearAuth &&
                    ((!state.summaryError && !state.totalAccomplishments) ||
                      (!state.accomplishmentsError &&
                        state.accomplishmentsByCategory === null)) ? (
                      <Loader />
                    ) : (
                      ""
                    )}
                    <div id="facultyHtml" className="faculty-html-container">
                      {state.reportingYear && (
                        <Header
                          reportingYear={state.reportingYear.name}
                          showDownloadBtn={
                            !state.accomplishmentsError || !state.summaryError
                          }
                        />
                      )}
                      {auth && state.reportingYearAuth ? (
                        <section
                          className="-mainheader -withbreadcrumbs only-breadcrumbs body-padding"
                          id="page-header"
                        >
                          <Accomplishments />
                          {state.accomplishmentsByCategory &&
                            Object.keys(
                              state.accomplishmentsByCategory.accomplishments
                            ).map((accomplishmentName: string) => {
                              return (
                                state.accomplishmentsByCategory && (
                                  <AccomplishmentsByCategory
                                    accomplishmentByCategory={
                                      state.accomplishmentsByCategory
                                        .accomplishments[accomplishmentName]
                                    }
                                    category={accomplishmentName}
                                    key={accomplishmentName}
                                  />
                                )
                              );
                            })}
                          {state.accomplishmentsError && (
                            <div className="html-border">
                              <ApiError />
                            </div>
                          )}
                        </section>
                      ) : (
                        <UnauthorizedComponent />
                      )}
                    </div>
                  </IntlProvider>
                )}
              </main>
            )}
            {state.reportingYearError && <ApiError />}
          </div>
        </div>
      </div>
    </div>
  );
}

interface AccomplishmentsByCategoryProps {
  accomplishmentByCategory: AccomplishmentData;
  category: string;
}
const AccomplishmentsByCategory = ({
  accomplishmentByCategory,
  category
}: AccomplishmentsByCategoryProps) => {
  const heading = useIntl().formatMessage(
    { id: "app.faculty.htmlChartLabel" },
    { category: category }
  );

  const categoryColumnHeader = useIntl().formatMessage(
    { id: "app.faculty.category" },
    { category: category }
  );

  return (
    <div className="html-border">
      <div className="category-heading-label">{heading}</div>
      {accomplishmentByCategory && (
        <CategoryStats
          category={category}
          accomplishmentByCategory={accomplishmentByCategory}
        />
      )}
      <div className="table-border">
        {accomplishmentByCategory && (
          <FacultySuccessCollegesTable
            accomplishmentByCategory={accomplishmentByCategory}
            categoryColumnHeader={categoryColumnHeader}
          />
        )}
      </div>
    </div>
  );
};

const CategoryStats = ({
  accomplishmentByCategory,
  category
}: AccomplishmentsByCategoryProps) => {
  const total = Object.keys(accomplishmentByCategory).length;
  const count = Object.keys(accomplishmentByCategory).reduce((acc, key) => {
    const total = accomplishmentByCategory[key]["Total"];
    return (acc = acc + (total > 0 ? 1 : 0));
  }, 0);
  return (
    <div className="category-stats-count">
      {translate("app.faculty.categoryStatsLabel", {
        count: count,
        total: total,
        category: category
      })}
    </div>
  );
};
